import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import validator from '@/constant/valitator';
import media from '@/styles/media';
import theme from '@/styles/theme';
import Message from '@/constant/message';
import putUserChangePassword from '@/fetches/user/user-profile/put-user-change-password';
import useAlert from '@/hooks/use-alert';
import { AppStore } from '@/store';

import Modal from '@/components/common/common-modal';
import { Logo } from '../common/auth-layout/logo';
import { LayoutWrapper, Wrapper } from '../common/new-auth-layout';
import { BalloonModal } from '../common/balloon-modal';
import { Input } from '../common/new-auth-input';
import { BasicButton, ButtonDiv } from '../common/new-auth-button';
import { Title } from '../common/new-auth-text';
import MobileLogo from '../common/auth-layout/mobile-logo';
import { hideSocialPwModal } from '.';

interface SocialPasswordProps{
  onSuccess?: () => void;
  onClose?: () => void;
}

const SocialPassword: React.FC<SocialPasswordProps> = ({ onSuccess, onClose }) => {
  const [pw, setPw] = useState<string>('');
  const [confirmPw, setConfirmPw] = useState<string>('');

  const [isPw, setIsPw] = useState<boolean>();
  const [isConfirm, setIsConfirm] = useState<boolean>();

  const [pwModal, setPwModal] = useState<string>('');

  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { alert } = useAlert();

  // 형식 검사를 통과한 경우에만 확인 버튼 활성화
  useEffect(() => {
    const check = isPw === true && pw === confirmPw;
    setIsValid(check);
  }, [confirmPw, isPw, pw]);

  // Check: 비밀번호 변경 로직으로 적용
  // -> 일단은 비어있는 정보를 채운다는 목적으로 만든 기능이기 때문에 변경으로 해두고 추후 정보 추가 시 수정
  const onClickSubmit = useCallback(async () => {
    setIsLoading(true);
    // 기존 비밀번호가 공백, null, undefined일 때 통과됨
    const result = await putUserChangePassword({ password: pw, prePassword: '' });

    if (!result) {
      const checkAlert = await alert(t(Message.비밀번호변경이완료되었습니다));
      if (checkAlert) {
        // 비밀번호 입력창을 닫아줘야함
        hideSocialPwModal();
      }
    } else if (result === '현재 비밀번호가 올바르지 않습니다.') {
      await alert(t(Message.기존비밀번호가존재합니다));
    } else {
      await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
    setIsLoading(false);
  }, [alert, pw, t]);

  const validatePassword = useCallback((value: string) => {
    const { err, args } = validator.checkPassword(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      if (args?.valueKeys) {
        return t(err, { value: args.valueKeys.map((v) => t(v)).join('\n') }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangePwInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validatePassword(value);
    setPw(value);
    if (value.length >= 3) {
      setIsPw(valid === '');
      if (valid !== '') {
        setPwModal(valid);
      }
    } else if (value.length < 1) {
      setPwModal('');
      setIsPw(undefined);
    }
  }, [validatePassword]);

  const onFocusInPw = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validatePassword(value);
    if (valid !== '') {
      setPwModal(valid);
    }
  }, [validatePassword]);

  const onFocusOutPw = useCallback(() => {
    setPwModal('');
  }, []);

  const onChangeConfirmInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > 0) {
      setConfirmPw(value);
      setIsConfirm(pw === value);
    } else {
      setIsConfirm(undefined);
    }
  }, [pw]);

  const handleOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onClickSubmit();
    }
  }, [onClickSubmit]);

  return (
    <Modal show closeOnClickOverlay={false} backgroundOpacity={1}>
      <PasswordWrapper>
        <Background>
          <LeftDiv>
            <TextContainer>
              <Logo />
              <LeftLargeText>
                Digital
                <br />
                Dynamic
                <br />
                Interactive
              </LeftLargeText>
              <LeftSubText>
                STEAM Education
                <br />
                Contents Platform
              </LeftSubText>
            </TextContainer>
            <ImageContainer>
              <RocketImg src="/assets/images/auth/rocket.png" alt="rocket image" />
              <CloudImg src="/assets/images/auth/cloud.png" alt="cloud image" />
            </ImageContainer>
          </LeftDiv>
        </Background>
        <LayoutWrapper>
          <ContentDiv>
            <InputWrapper>
              <Check>
                <LogoDiv>
                  <MobileLogo />
                </LogoDiv>
                <Title>{t(Message.비밀번호입력)}</Title>
                <TitleSubText>{t(Message.보다안전한서비스이용을위해비밀번호를설정해주세요)}</TitleSubText>
                <Input
                  autoFocus
                  onChange={onChangePwInput}
                  placeholder={t(Message.비밀번호를입력하세요)}
                  validate={isPw}
                  onBlur={onFocusOutPw}
                  onFocus={onFocusInPw}
                  type="password"
                  autoComplete="password"
                />
                {(pwModal && isPw !== undefined) && <BalloonModal fade={isPw} text={pwModal} />}
                <Input
                  onChange={onChangeConfirmInput}
                  disabled={!isPw}
                  onKeyPress={handleOnKeyPress}
                  placeholder={t(Message.비밀번호확인)}
                  type="password"
                  validate={isConfirm} // 여기 다시 check
                />
                {isConfirm && <BalloonModal fade={isConfirm} text={t(Message.비밀번호와같지않습니다)} />}
                <NextButton
                  type="button"
                  onClick={onClickSubmit}
                  disabled={!isValid}
                  active={isValid}
                >
                  {t(Message.확인)}
                </NextButton>
              </Check>
            </InputWrapper>

          </ContentDiv>
        </LayoutWrapper>
      </PasswordWrapper>
    </Modal>
  );
};

const SocialPasswordModal: React.FC<SocialPasswordProps> = (props) => (
  <Provider store={AppStore}>
    <SocialPassword {...props} />
  </Provider>
);

export default SocialPasswordModal;

const PasswordWrapper = styled(Wrapper)`
  height: 100vh;
`;

const Background = styled.div`
  width: 100%;
  max-width: 760px;
  height: 100%;
  display: flex;
  justify-content:flex-end ;
  align-items: center;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('/assets/images/auth/login-left-bg.png');
  ${media.lg`
    display: none;
  `}
  `;

const LeftDiv = styled.div`
  width: 100%;
  max-width: 500px;
  height: 100%;
  max-height: 820px;
  background-color: #FAF7FD;
  border-radius: 50px 0px 0px 50px;
  margin-left: 60px;
`;

const ContentDiv = styled.div`
  width: 100%;
  max-width: 860px;
  height: 100%;
  max-height: 819px;
  border-radius: 0px 50px 50px 0px ;
  box-sizing: border-box;
  background-color: white;
  margin-right: 60px;
  ${media.lg`
    background-color: #f8f9ff;
    margin-right: 0;
    max-width: 100%;
    max-height: 100%;
    
    border-radius: unset;
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  font-family: ${theme.fontFamily.regular};
  font-style: normal;
  height: 100%;
  ${media.lg`
    align-items: center;
    padding: 0 15px;
  `}
`;

const Check = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 420px;
  padding-left: 120px;
  padding-right: 50px;
  justify-content: center;
  ${media.xxl`
    padding-left: 80px ;
  `}
  ${media.xl`
    padding-left: 40px ;
  `}
  ${media.lg`
    padding: 0;
    align-items: center;
    width: 100%;
    &.teacher-signup{
      gap: 10px;
    }
  `}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 12rem;  
  padding: 53px 80px;
`;

const LeftLargeText = styled.div`
  margin-top:5rem; 
  font-family: ${theme.fontFamily.extraBold};
  font-style: normal;
  font-size: 38px;
  line-height: 42px;
`;

const LeftSubText = styled.div`
  margin-top: 1.5rem;   
  font-family:  ${theme.fontFamily.regular};
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
`;

const ImageContainer = styled.div`
  position: relative;
  flex:1;
  ${media.lg`
    display: none;
  `}
`;

const RocketImg = styled.img`
  position: absolute;
  width: 165px;
  top: 30px;
  left: 269px;
  transform: rotate(15deg);
  ${media.xl`
    transform: scale( 0.85) rotate(15deg);
    transition: all 0.2s linear;
  `}
`;

const CloudImg = styled.img`
  position: absolute;
  width: 498px;
  left: 10px;
  top: 100px;
  ${media.xl`
    transform: scale( 0.85 );
    transition: all 0.2s linear;
  `}
`;

interface ButtonProps {
  active?: boolean;
}
const Button = styled(BasicButton)<ButtonProps>`
  width: 100%;
  max-width: 420px;
  margin: 110px 0px 25px;
  padding: 16px 74px;
  background-color: ${(props) => (props.active ? theme.color.purple : '#4B586A')};
  ${media.lg`
    padding: 16px 0;
    margin: 80px 0px 25px;
  `}
`;

const NextButton = styled(Button)`
  background-color: ${theme.color.purple};
  :disabled{
    background-color: #F0F0F0;
    color: ${theme.color.darkGray};
  }
`;

const TitleSubText = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 22px;
  padding-bottom: 20px;
  ${media.lg`
    font-family: ${theme.fontFamily.medium};
    width: 100%;
    padding-bottom: 22px;
  `}
`;

const LogoDiv = styled(ButtonDiv)`
  padding: 50px 50px 73px 0px;
`;
